import { useEffect, useState } from "react";
import { Cpu, DollarSign, TrendingUp, Coins, Save } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const FEATURE_LABELS = {
  advisor_deep: "Consultanță AI plătită (deep)",
  advisor_free: "Consultanță AI gratuită",
  advisor_free_anon: "Consultanță AI (vizitatori)",
  classify: "Auto-categorizare anunțuri",
  translate_listing: "Traducere anunț",
  translate_fields: "Traducere titlu + descriere",
  translate_catalog: "Traducere catalog",
  news_generate: "Generare articol SEO",
  news_translate: "Traducere articol SEO",
  valuation: "Estimare preț (gratuit)",
  valuation_premium: "Raport AI preț (premium)",
};

const fmtUsd = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString("ro-RO");

export default function AiCostPanel() {
  const [data, setData] = useState(null);
  const [rates, setRates] = useState({});
  const [saving, setSaving] = useState(false);
  const [upc, setUpc] = useState("0.20");
  const [savingUpc, setSavingUpc] = useState(false);

  const load = () => api.get("/admin/ai-usage").then((r) => { setData(r.data); setRates(r.data.rates || {}); setUpc(String(r.data.usd_per_credit ?? 0.20)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setRate = (model, key) => (e) => setRates((p) => ({ ...p, [model]: { ...p[model], [key]: e.target.value } }));

  const saveRates = async () => {
    setSaving(true);
    const clean = {};
    for (const [m, r] of Object.entries(rates)) clean[m] = { in: Number(r.in) || 0, out: Number(r.out) || 0 };
    try { await api.put("/admin/ai-rates", { ai_model_rates: clean }); toast.success("Tarife AI salvate"); load(); }
    catch { toast.error("Eroare la salvare"); }
    finally { setSaving(false); }
  };

  const saveUpc = async () => {
    setSavingUpc(true);
    try { await api.put("/admin/ai-credit-rate", { usd_per_credit: Number(upc) }); toast.success("Curs credite salvat"); load(); }
    catch { toast.error("Curs invalid"); }
    finally { setSavingUpc(false); }
  };

  if (!data) return null;

  const fmtCred = (n) => Number(n || 0).toLocaleString("ro-RO", { maximumFractionDigits: 3 });
  const cards = [
    { icon: Cpu, label: "Apeluri AI (total)", value: fmtInt(data.total.calls), sub: `${fmtInt(data.total.total_tokens)} tokeni · Ø ${fmtInt(data.avg_tokens_per_call)}/apel` },
    { icon: DollarSign, label: "Cost real AI (estimat)", value: fmtUsd(data.total.cost_usd), sub: `≈ ${fmtCred(data.cost_credits)} credite Emergent` },
    { icon: Coins, label: "Venituri din tokenuri", value: `$${fmtInt(data.revenue_usd)}`, sub: `≈ ${fmtCred(data.revenue_credits)} credite · ${data.paid_calls} apeluri plătite` },
    { icon: TrendingUp, label: "Marjă brută reală", value: data.gross_margin_pct == null ? "—" : `${data.gross_margin_pct}%`, sub: `profit ${fmtUsd(data.gross_profit_usd)}` },
  ];

  return (
    <div data-testid="ai-cost-panel">
      <h2 className="font-black text-xl flex items-center gap-2 mb-1"><Cpu className="w-5 h-5 text-orange-600" /> Costuri reale AI vs. venituri</h2>
      <p className="text-sm text-slate-500 mb-5">Consumul real de tokeni măsurat la fiecare apel AI (Gemini), convertit în cost estimat pe baza tarifelor de mai jos. Compară costul real cu veniturile din pachetele de tokeni pentru marja adevărată.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="border border-slate-200 p-4" data-testid={`ai-card-${c.label}`}>
            <c.icon className="w-5 h-5 text-slate-400" />
            <div className="text-2xl font-black mt-2">{c.value}</div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{c.label}</div>
            <div className="text-xs text-slate-400 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <h3 className="font-bold mb-3">Defalcare pe funcții</h3>
      <div className="border border-slate-200 overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Funcție</th>
              <th className="text-right px-3 py-2">Apeluri</th>
              <th className="text-right px-3 py-2">Tokeni in</th>
              <th className="text-right px-3 py-2">Tokeni out</th>
              <th className="text-right px-3 py-2">Total tokeni</th>
              <th className="text-right px-3 py-2">Cost real</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Object.keys(data.by_feature).length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Încă niciun apel AI înregistrat.</td></tr>
            ) : Object.entries(data.by_feature).map(([f, v]) => (
              <tr key={f} data-testid={`ai-feature-${f}`}>
                <td className="px-3 py-2 font-semibold text-slate-900">{FEATURE_LABELS[f] || f}</td>
                <td className="text-right px-3 py-2">{fmtInt(v.calls)}</td>
                <td className="text-right px-3 py-2">{fmtInt(v.input_tokens)}</td>
                <td className="text-right px-3 py-2">{fmtInt(v.output_tokens)}</td>
                <td className="text-right px-3 py-2 font-mono">{fmtInt(v.total_tokens)}</td>
                <td className="text-right px-3 py-2 font-bold text-orange-600">{fmtUsd(v.cost_usd)}<div className="text-[10px] text-slate-400 font-normal">≈ {fmtCred(v.cost_credits)} cr</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-slate-200 p-5 mb-6" data-testid="ai-credit-rate-form">
        <h3 className="font-bold mb-1">Curs credite Emergent</h3>
        <p className="text-sm text-slate-500 mb-3">Câți USD costă 1 credit Emergent (implicit $0.20 = 100 credite / $20). Costurile se afișează și în credite pe baza acestei valori.</p>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">USD / credit</label>
            <Input type="number" step="0.01" value={upc} onChange={(e) => setUpc(e.target.value)} className="rounded-none mt-1 w-32" data-testid="ai-credit-rate-input" />
          </div>
          <button onClick={saveUpc} disabled={savingUpc} className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 disabled:opacity-50 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="ai-credit-rate-save">
            <Save className="w-4 h-4" /> {savingUpc ? "Se salvează…" : "Salvează"}
          </button>
        </div>
      </div>

      <div className="border border-slate-200 p-5" data-testid="ai-rates-form">
        <h3 className="font-bold mb-1">Tarife model (USD / 1.000.000 tokeni)</h3>
        <p className="text-sm text-slate-500 mb-4">Ajustează tarifele ca să reflecte creditele Emergent consumate real. Costurile estimate se recalculează cu valorile de aici.</p>
        <div className="space-y-3">
          {Object.entries(rates).map(([model, r]) => (
            <div key={model} className="grid grid-cols-[1fr_120px_120px] gap-3 items-end" data-testid={`ai-rate-${model}`}>
              <div className="font-mono text-sm text-slate-700 pb-2 truncate">{model}</div>
              <div><label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Input</label><Input type="number" step="0.01" value={r.in ?? ""} onChange={setRate(model, "in")} className="rounded-none mt-1" data-testid={`ai-rate-${model}-in`} /></div>
              <div><label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Output</label><Input type="number" step="0.01" value={r.out ?? ""} onChange={setRate(model, "out")} className="rounded-none mt-1" data-testid={`ai-rate-${model}-out`} /></div>
            </div>
          ))}
        </div>
        <button onClick={saveRates} disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-700 disabled:opacity-50 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors" data-testid="ai-rates-save">
          <Save className="w-4 h-4" /> {saving ? "Se salvează…" : "Salvează tarifele"}
        </button>
      </div>
    </div>
  );
}
